import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ARTICLES, getArticleBySlug } from "@/content/blog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { ArrowLeft, ArrowRight, Clock, CheckCircle2 } from "lucide-react";

function renderBlock(block, i) {
  switch (block.type) {
    case "h2":
      return (
        <h2
          key={i}
          className="font-heading text-3xl md:text-4xl text-white uppercase tracking-tight mt-12 mb-4"
        >
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="font-heading text-xl md:text-2xl text-gold uppercase tracking-wide mt-8 mb-3">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p
          key={i}
          className="text-white/80 text-base md:text-lg leading-relaxed mb-5"
          dangerouslySetInnerHTML={{ __html: block.text }}
        />
      );
    case "ul":
      return (
        <ul key={i} className="space-y-2 mb-6 mt-1">
          {block.items.map((it, idx) => (
            <li key={idx} className="flex items-start gap-3 text-white/80 text-base md:text-lg leading-relaxed">
              <CheckCircle2 size={18} className="text-gold mt-1 shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: it }} />
            </li>
          ))}
        </ul>
      );
    case "cta":
      return (
        <div
          key={i}
          className="my-10 bg-gradient-to-br from-gold/15 via-gold/5 to-transparent border border-gold/40 rounded-lg p-6 md:p-8"
        >
          <p className="text-white text-lg font-semibold mb-4 leading-snug">{block.text}</p>
          <Link
            to="/#quote"
            className="inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest px-6 py-3 rounded hover:bg-gold-hover transition-all hover:shadow-[0_0_25px_rgba(255,215,0,0.4)]"
          >
            Get a Free Quote
            <ArrowRight size={16} />
          </Link>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return;

    document.title = `${article.title} | Superstate LLC`;
    const setMeta = (selector, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute("content", value);
    };
    setMeta('meta[name="description"]', article.description);
    setMeta('meta[name="keywords"]', article.keywords.join(", "));
    setMeta('meta[property="og:title"]', article.title);
    setMeta('meta[property="og:description"]', article.description);
    setMeta('meta[property="og:url"]', `https://superstatedispatch.com/blog/${article.slug}`);
    setMeta('meta[property="og:image"]', `https://superstatedispatch.com${article.cover}`);
    setMeta('meta[property="og:type"]', "article");
    setMeta('meta[name="twitter:title"]', article.title);
    setMeta('meta[name="twitter:description"]', article.description);

    // Inject Article schema
    const existing = document.getElementById("article-schema");
    if (existing) existing.remove();
    const schema = document.createElement("script");
    schema.id = "article-schema";
    schema.type = "application/ld+json";
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.description,
      author: { "@type": "Organization", name: "Superstate LLC" },
      publisher: {
        "@type": "Organization",
        name: "Superstate LLC",
        logo: {
          "@type": "ImageObject",
          url: "https://superstatedispatch.com/logos/truck-mark.jpg",
        },
      },
      datePublished: article.published,
      dateModified: article.published,
      mainEntityOfPage: `https://superstatedispatch.com/blog/${article.slug}`,
      image: `https://superstatedispatch.com${article.cover}`,
      keywords: article.keywords.join(", "),
    });
    document.head.appendChild(schema);

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `https://superstatedispatch.com/blog/${article.slug}`;

    window.scrollTo(0, 0);

    return () => {
      const s = document.getElementById("article-schema");
      if (s) s.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="relative min-h-screen">
      <Navbar />

      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm uppercase tracking-widest mb-8"
          >
            <ArrowLeft size={14} /> All Articles
          </Link>

          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/50 mb-5">
            <span className="text-gold font-semibold">
              {new Date(article.published).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span className="flex items-center gap-1">
              <Clock size={12} /> {article.readTime}
            </span>
          </div>

          <h1
            data-testid="blog-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-[0.98]"
          >
            {article.title}
          </h1>

          <p className="mt-6 text-white/70 text-lg md:text-xl leading-relaxed italic border-l-2 border-gold pl-5">
            {article.intro}
          </p>

          <div className="mt-10 rounded-lg overflow-hidden border border-ink-line">
            <img
              src={article.cover}
              alt={article.coverAlt || article.title}
              className="w-full aspect-video object-cover"
            />
          </div>

          <div className="mt-12 font-body">
            {article.blocks.map((b, i) => renderBlock(b, i))}
          </div>

          {/* End-of-article CTA */}
          <div className="mt-14 bg-ink-card border border-ink-line rounded-lg p-8 text-center">
            <div className="text-gold text-xs uppercase tracking-[0.3em] font-bold mb-3">
              Your Next Step
            </div>
            <h3 className="font-heading text-3xl md:text-4xl text-white uppercase">
              Ready to move more freight?
            </h3>
            <p className="mt-3 text-white/70">
              Get a free dispatch quote from Superstate LLC. No contract, no obligation, 24-hour response.
            </p>
            <Link
              to="/#quote"
              className="mt-6 inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest px-8 py-3.5 rounded hover:bg-gold-hover transition-all hover:shadow-[0_0_25px_rgba(255,215,0,0.4)]"
            >
              Get a Free Quote
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Related posts */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="text-gold text-xs uppercase tracking-[0.3em] font-bold mb-4">
            Keep Reading
          </div>
          <h3 className="font-heading text-3xl md:text-4xl text-white uppercase mb-8">
            More From the Blog
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                className="group bg-ink-card border border-ink-line hover:border-gold/50 rounded-lg overflow-hidden transition-all hover:-translate-y-1"
              >
                <div className="aspect-video bg-ink overflow-hidden">
                  <img
                    src={r.cover}
                    alt={r.coverAlt || r.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold mb-2">
                    {r.readTime}
                  </div>
                  <h4 className="font-heading text-lg text-white uppercase leading-tight group-hover:text-gold transition-colors">
                    {r.title}
                  </h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <Footer />
      <ChatWidget />
    </div>
  );
}
